import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { clsx } from 'clsx';
import { Check, ChevronDownIcon, Search, X } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SelectOption } from '../Select';
import Field, { type FieldProps } from '@/components/common/Field';
import InputGroup from '@/components/common/Input/InputGroup';

export interface MultiSelectProps extends Omit<FieldProps, 'children' | 'id'> {
    id?: string;
    name: string;
    options: SelectOption[];
    value?: (string | number | null)[];
    onChange?: (value: (string | number | null)[]) => void;
    placeholder?: string;
    disabled?: boolean;
    searchable?: boolean;
    searchPlaceholder?: string;
    maxSelected?: number;
    clearable?: boolean;
    inputState?: 'valid' | 'invalid' | 'normal';
    showSearchIcon?: boolean;
    enableSelectAll?: boolean;
}

const MultiSelect = ({
    id,
    name,
    label,
    helpElement,
    errorElement,
    required,
    options,
    value = [],
    onChange,
    placeholder,
    disabled = false,
    searchable = false,
    searchPlaceholder,
    maxSelected,
    clearable = true,
    inputState = 'normal',
    showSearchIcon = false,
    enableSelectAll = true,
    description,
}: MultiSelectProps) => {
    const { t } = useTranslation('common');
    const resolvedPlaceholder = placeholder ?? t('multiSelect.placeholder');
    const resolvedSearchPlaceholder = searchPlaceholder ?? t('multiSelect.searchPlaceholder');
    const elementId = useId();
    const computedId = id || elementId;
    const [searchTerm, setSearchTerm] = useState('');

    const selectedOptions = useMemo(() => options.filter(o => value.includes(o.value)), [options, value]);

    const filtered = useMemo(() => {
        if (!searchable || !searchTerm) {
            return options;
        }
        const term = searchTerm.toLowerCase();
        return options.filter(o => o.label.toLowerCase().includes(term));
    }, [options, searchable, searchTerm]);

    const toggleValue = (val: SelectOption['value']) => {
        if (value.includes(val)) {
            const next = value.filter(v => v !== val);
            onChange?.(next);
        } else {
            if (maxSelected && value.length >= maxSelected) {
                return;
            }
            onChange?.([...value, val]);
        }
    };

    const clearAll = () => onChange?.([]);

    const optionLabel =
        selectedOptions.length === 0 ? resolvedPlaceholder : selectedOptions.map(o => o.label).join(', ');

    const selectAllOption = useMemo(() => {
        const isAllSelected =
            options.every(opt => value.includes(opt.value)) || (maxSelected && value.length >= maxSelected);
        return (
            <>
                <ListboxOption
                    onClick={e => {
                        e.preventDefault();
                        if (isAllSelected) {
                            onChange?.([]);
                        } else {
                            onChange?.([...options.map(opt => opt.value)]);
                        }
                    }}
                    key={'all'}
                    value={'__all__'}
                    className={clsx(
                        'group relative flex cursor-default items-center gap-2 px-3 py-2 select-none',
                        'text-neutral-800 data-[focus]:bg-teal-600 data-[focus]:text-white'
                    )}
                >
                    {/* Checkbox */}
                    <span
                        className={clsx(
                            'flex size-4 items-center justify-center rounded-sm border text-white transition-colors',
                            isAllSelected
                                ? 'border-teal-600 bg-teal-600'
                                : 'border-neutral-300 bg-white text-transparent',
                            'group-data-[focus]:border-white'
                        )}
                        aria-hidden="true"
                    >
                        <Check className="size-4" />
                    </span>
                    <span className="block truncate">{t('multiSelect.selectAll')}</span>
                </ListboxOption>
                <div className="mx-3 border-b border-b-neutral-100"></div>
            </>
        );
    }, [maxSelected, onChange, options, t, value]);

    return (
        <Listbox
            multiple
            value={value}
            onChange={(vals: (string | number | null)[]) => onChange?.(vals)}
            disabled={disabled}
        >
            <Field
                id={computedId}
                name={name}
                label={label}
                helpElement={helpElement}
                errorElement={errorElement}
                required={required}
                description={description}
            >
                <ListboxButton
                    as={InputGroup}
                    inputState={inputState}
                    data-cy="multi-select"
                    data-cy-id={computedId}
                    data-empty={!selectedOptions.length || undefined}
                    className={clsx(
                        'cursor-pointer data-disabled:cursor-not-allowed data-empty:text-neutral-400',
                        disabled ? '!bg-gray-50 !text-neutral-500' : '!bg-white'
                    )}
                >
                    <div className="flex w-full items-center gap-2">
                        {/* Selected chips */}
                        <div className="flex min-h-[38px] grow flex-wrap items-center gap-1 py-1">
                            {selectedOptions.length === 0 && (
                                <span className="truncate text-base leading-6 font-medium text-neutral-400">
                                    {optionLabel}
                                </span>
                            )}
                            {selectedOptions.map(opt => (
                                <span
                                    key={opt.value ?? '__null'}
                                    className={clsx(
                                        'group flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ring-1',
                                        disabled
                                            ? 'bg-neutral-200 text-neutral-600 ring-neutral-300'
                                            : 'bg-teal-50 text-teal-700 ring-teal-200'
                                    )}
                                >
                                    {opt.label}
                                    {!disabled && (
                                        <button
                                            type="button"
                                            onClick={e => {
                                                e.stopPropagation();
                                                toggleValue(opt.value);
                                            }}
                                            className="text-teal-500 hover:text-teal-700"
                                            aria-label={t('multiSelect.aria.removeItem', { item: opt.label })}
                                        >
                                            <X className="size-4" />
                                        </button>
                                    )}
                                </span>
                            ))}
                        </div>
                        {!disabled && clearable && selectedOptions.length > 0 && (
                            <button
                                type="button"
                                aria-label={t('multiSelect.aria.clear')}
                                onClick={e => {
                                    e.stopPropagation();
                                    clearAll();
                                }}
                                className="shrink-0 rounded p-1 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-600"
                            >
                                <X className="size-4" />
                            </button>
                        )}
                        {!disabled && (
                            <span className="shrink-0 text-neutral-500 select-none">
                                {showSearchIcon ? (
                                    <Search className="size-4" strokeWidth={2} />
                                ) : (
                                    <ChevronDownIcon className="size-4" strokeWidth={2} />
                                )}
                            </span>
                        )}
                    </div>
                </ListboxButton>

                <ListboxOptions
                    anchor="bottom start"
                    className={clsx(
                        'absolute z-10 mt-1 max-h-60 overflow-auto rounded-md bg-white text-sm shadow-lg ring-1 ring-black/5 focus:outline-none',
                        'data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in data-[closed]:data-[leave]:opacity-0'
                    )}
                    transition
                >
                    {searchable && (
                        <div className="sticky top-0 border-b border-neutral-200 bg-white px-3 py-2">
                            <div className="relative inline-flex h-9 w-full items-center gap-2.5 rounded-md bg-white px-2.5 py-[5px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-neutral-300">
                                <input
                                    className="flex-1 border-0 bg-transparent text-base font-medium text-neutral-800 placeholder:text-neutral-400"
                                    placeholder={resolvedSearchPlaceholder}
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    autoFocus
                                    style={{ outline: 'none', boxShadow: 'none' }}
                                />
                                <Search className="size-4 text-neutral-400" />
                            </div>
                        </div>
                    )}

                    <div className="max-h-56 overflow-y-auto py-1">
                        {filtered.length === 0 && (
                            <div className="px-4 py-2 text-neutral-500">{t('multiSelect.noOptions')}</div>
                        )}
                        {enableSelectAll && filtered.length > 0 && selectAllOption}
                        {filtered.map((opt, index) => {
                            const isSelected = value.includes(opt.value);
                            const disabledOption = !!maxSelected && !isSelected && value.length >= maxSelected;
                            return (
                                <>
                                    <ListboxOption
                                        key={opt.value ?? '__null'}
                                        value={opt.value}
                                        disabled={disabledOption}
                                        className={clsx(
                                            'group relative flex cursor-default items-center gap-2 px-3 py-2 select-none',
                                            'text-neutral-800 data-[focus]:bg-teal-600 data-[focus]:text-white',
                                            disabledOption && 'opacity-50'
                                        )}
                                    >
                                        {/* Checkbox */}
                                        <span
                                            className={clsx(
                                                'flex size-4 items-center justify-center rounded-sm border text-white transition-colors',
                                                isSelected
                                                    ? 'border-teal-600 bg-teal-600'
                                                    : 'border-neutral-300 bg-white text-transparent',
                                                'group-data-[focus]:border-white'
                                            )}
                                            aria-hidden="true"
                                        >
                                            <Check className="size-4" />
                                        </span>
                                        <span className="block truncate">{opt.label}</span>
                                        {disabledOption && (
                                            <span className="ml-auto text-[10px] tracking-wide text-neutral-400 uppercase">
                                                {t('multiSelect.maxTag')}
                                            </span>
                                        )}
                                    </ListboxOption>
                                    {index < filtered.length - 1 && (
                                        <div className="mx-3 border-b border-b-neutral-100"></div>
                                    )}
                                </>
                            );
                        })}
                    </div>
                </ListboxOptions>
            </Field>
        </Listbox>
    );
};

export default MultiSelect;
